//src/services/surveyCampaigns.ts
import { api } from "@/lib/apiClient";
import type {
  SurveyCampaign,
  SurveyCampaignAudiencePreviewItem,
  SurveyCampaignCreateIn,
  SurveyCampaignDetail,
  SurveyCampaignParticipantActionOut,
  SurveyCampaignParticipantAddIn,
  SurveyCampaignPublishOut,
  SurveyCampaignRecipientEvent,
  SurveyCampaignScheduleUpdateIn,
  SurveyCampaignToken,
  SurveyCampaignUpdateIn,
} from "@/types/gro";

export interface SurveyCampaignEmailSettings {
  tenant_id: string;
  host: string;
  port: number;
  username: string;
  security_mode: "ssl_tls" | "starttls" | "none";
  sender_name: string;
  sender_email: string;
  reply_to?: string | null;
  connection_timeout_seconds: number;
  batch_size: number;
  rate_limit_per_minute: number;
  is_active: boolean;
  has_password: boolean;
  updated_at: string;
}

export interface SurveyCampaignEmailSettingsInput {
  host: string;
  port: number;
  username: string;
  password: string;
  security_mode: "ssl_tls" | "starttls" | "none";
  sender_name: string;
  sender_email: string;
  reply_to?: string | null;
  connection_timeout_seconds: number;
  batch_size: number;
  rate_limit_per_minute: number;
  is_active: boolean;
}

export interface SurveyCampaignEmailSettingsTestOut {
  success: boolean;
  message: string;
}

export interface SurveyCampaignInviteEmailJob {
  id: string;
  campaign_id: string;
  campaign_title: string;
  status: "pending" | "processing" | "completed" | "completed_with_errors" | "failed";
  template_mode: "identified" | "anonymous" | "pseudonymized";
  total_recipients: number;
  total_pending: number;
  total_processing: number;
  total_sent: number;
  total_failed: number;
  requested_at: string;
  started_at?: string | null;
  finished_at?: string | null;
  requested_by?: string | null;
  last_error?: string | null;
}

export interface SurveyCampaignInviteEmailDispatchOut {
  job: SurveyCampaignInviteEmailJob;
  message: string;
}

export interface SurveyCampaignRecipientEmailActionOut {
  campaign_id: string;
  recipient_id: string;
  status_send_email: string;
  message: string;
  sent_at?: string | null;
  last_error?: string | null;
}


export async function listSurveyCampaigns(params?: {
  status?: string;
  q?: string;
}) {
  return api.get<SurveyCampaign[]>("/survey-campaigns", {
    params: {
      status: params?.status || undefined,
      q: params?.q || undefined,
    },
  });
}

export async function getSurveyCampaign(id: string) {
  return api.get<SurveyCampaignDetail>(`/survey-campaigns/${id}`);
}

export async function createSurveyCampaign(payload: SurveyCampaignCreateIn) {
  return api.post<SurveyCampaignDetail>("/survey-campaigns", payload);
}

export async function updateSurveyCampaign(
  id: string,
  payload: SurveyCampaignUpdateIn,
) {
  return api.put<SurveyCampaignDetail>(`/survey-campaigns/${id}`, payload);
}

export async function updateSurveyCampaignSchedule(
  id: string,
  payload: SurveyCampaignScheduleUpdateIn,
) {
  return api.patch<SurveyCampaignDetail>(`/survey-campaigns/${id}/schedule`, payload);
}

export async function previewSurveyCampaignAudience(
  payload: SurveyCampaignCreateIn,
) {
  return api.post<SurveyCampaignAudiencePreviewItem[]>(
    "/survey-campaigns/audience-preview",
    payload,
  );
}

export async function publishSurveyCampaign(id: string) {
  return api.post<SurveyCampaignPublishOut>(`/survey-campaigns/${id}/publish`, {});
}

export async function closeSurveyCampaign(id: string) {
  return api.post<SurveyCampaignPublishOut>(`/survey-campaigns/${id}/close`, {});
}

export async function reopenSurveyCampaign(id: string) {
  return api.post<SurveyCampaignPublishOut>(`/survey-campaigns/${id}/reopen`, {});
}

export async function listSurveyCampaignTokens(id: string) {
  return api.get<SurveyCampaignToken[]>(`/survey-campaigns/${id}/tokens`);
}

export async function addSurveyCampaignParticipant(
  campaignId: string,
  payload: SurveyCampaignParticipantAddIn,
) {
  return api.post<SurveyCampaignParticipantActionOut>(
    `/survey-campaigns/${campaignId}/participants`,
    payload,
  );
}

export async function deactivateSurveyCampaignParticipant(
  campaignId: string,
  recipientId: string,
) {
  return api.delete<SurveyCampaignParticipantActionOut>(
    `/survey-campaigns/${campaignId}/participants/${recipientId}`,
  );
}

export async function reactivateSurveyCampaignParticipant(
  campaignId: string,
  recipientId: string,
) {
  return api.post<SurveyCampaignParticipantActionOut>(
    `/survey-campaigns/${campaignId}/participants/${recipientId}/reactivate`,
    {},
  );
}

export async function listSurveyCampaignParticipantHistory(
  campaignId: string,
  recipientId: string,
) {
  return api.get<SurveyCampaignRecipientEvent[]>(
    `/survey-campaigns/${campaignId}/participants/${recipientId}/history`,
  );
}

export async function getSurveyCampaignEmailSettings() {
  return api.get<SurveyCampaignEmailSettings | null>("/survey-campaigns/email-settings");
}

export async function upsertSurveyCampaignEmailSettings(payload: SurveyCampaignEmailSettingsInput) {
  return api.put<SurveyCampaignEmailSettings>("/survey-campaigns/email-settings", payload);
}

export async function testSurveyCampaignEmailSettings() {
  return api.post<SurveyCampaignEmailSettingsTestOut>("/survey-campaigns/email-settings/test", {});
}

export async function sendSurveyCampaignInviteEmails(campaignId: string) {
  return api.post<SurveyCampaignInviteEmailDispatchOut>(
    `/survey-campaigns/${campaignId}/invite-emails/send`,
    {},
  );
}

export async function getLatestSurveyCampaignInviteEmailJob(campaignId: string) {
  return api.get<SurveyCampaignInviteEmailJob | null>(
    `/survey-campaigns/${campaignId}/invite-emails/jobs/latest`,
  );
}


export async function updateSurveyCampaignRecipientEmailStatus(
  campaignId: string,
  recipientId: string,
  statusSendEmail: "pending",
) {
  return api.patch<SurveyCampaignRecipientEmailActionOut>(
    `/survey-campaigns/${campaignId}/participants/${recipientId}/email-status`,
    { status_send_email: statusSendEmail },
  );
}

export async function sendSurveyCampaignRecipientInviteEmail(
  campaignId: string,
  recipientId: string,
) {
  return api.post<SurveyCampaignRecipientEmailActionOut>(
    `/survey-campaigns/${campaignId}/participants/${recipientId}/send-email`,
    {},
  );
}
